import React, { useState, useEffect, useCallback } from 'react';
import {
  getEvents, getEvent, createEvent, updateEvent, publishEvent,
  cancelEvent, archiveEvent, completeEvent, cloneEvent,
  generateRecurringInstances,
  estimateAudience,
  getQRCodeUrl, getRegistrationUrl,
  EVENT_CATEGORIES, EVENT_STATUSES,
  fmtDateTime
} from '../../../api/eventsApi';
import RegistrationFormBuilder from './RegistrationFormBuilder';
import {
  getEventProposals,
  approveProposal,
  declineProposal,
} from '../../../api/eventProposalsApi';
import {
  SINGAPORE_DISTRICTS,
  MEMBER_INTERESTS,
  MEMBER_EMPLOYMENT_STATUSES,
} from '../../../api/communityApi';

const TABS = [
  { key: 'allEvents',        label: 'All Events' },
  { key: 'qrComms',          label: 'QR & Comms' },
  { key: 'moderationQueue',  label: 'Moderation Queue' },
];

const CATEGORY_BADGE = {
  CIRCLE_TIME:         'bb-badge bb-badge--teal',
  TALK_WORKSHOP:       'bb-badge bb-badge--indigo',
  SOCIAL_RECREATIONAL: 'bb-badge bb-badge--orange',
  OTHER:               'bb-badge bb-badge--grey',
};

const STATUS_BADGE = {
  DRAFT:     'bb-badge bb-badge--grey',
  PUBLISHED: 'bb-badge bb-badge--green',
  CANCELLED: 'bb-badge bb-badge--red',
  COMPLETED: 'bb-badge bb-badge--blue',
  ARCHIVED:  'bb-badge bb-badge--grey',
};

const EMPTY_FORM = {
  title: '', category: 'CIRCLE_TIME', event_type: 'ONE_OFF',
  start_datetime: '', end_datetime: '',
  location: { name: '', address: '', postal_code: '' },
  description: '', tags: '',
  photo_consent_required: false, no_show_threshold: 3,
  volunteers_required: false, volunteers_needed: '',
  member_capacity: '', volunteer_capacity: '',
  attendee_tracking_enabled: false,
  speaker: { name: '', bio: '', headshot_url: '' },
  recurrence_rule: { frequency: 'MONTHLY', day_of_week: 'Monday', nth_occurrence: 1 },
};

const DAY_OPTIONS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/* ─── Audience Estimation Panel ───────────────────────────────────────────── */
function AudienceEstimation({ orgId }) {
  const [open, setOpen]             = useState(false);
  const [districts, setDistricts]   = useState([]);
  const [interests, setInterests]   = useState([]);
  const [empStatuses, setEmpStatuses] = useState([]);
  const [result, setResult]         = useState(null);
  const [loading, setLoading]       = useState(false);

  const toggleItem = (list, setList, val) => {
    setList(list.includes(val) ? list.filter(v => v !== val) : [...list, val]);
    setResult(null);
  };

  const handleEstimate = async () => {
    setLoading(true);
    try {
      const r = await estimateAudience(orgId, {
        districts:          districts,
        interests:          interests,
        employmentStatuses: empStatuses,
      });
      setResult(r);
    } catch { /* non-critical */ }
    finally { setLoading(false); }
  };

  return (
    <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: 14, marginBottom: 12 }}>
      <button
        type="button"
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#92400E', padding: 0, display: 'flex', alignItems: 'center', gap: 6 }}
        onClick={() => setOpen(v => !v)}
      >
        📊 Audience Estimation {open ? '▲' : '▼'}
        <span style={{ fontWeight: 400, fontSize: 12, color: '#6B7280', marginLeft: 4 }}>
          — filter your member base to gauge potential attendance
        </span>
      </button>

      {open && (
        <div style={{ marginTop: 14 }}>
          {/* Districts */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#374151', marginBottom: 6 }}>Districts (any match)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(SINGAPORE_DISTRICTS || []).map(d => (
                <button
                  key={d} type="button"
                  onClick={() => toggleItem(districts, setDistricts, d)}
                  style={{
                    padding: '3px 10px', borderRadius: 12, fontSize: 11, cursor: 'pointer',
                    background: districts.includes(d) ? '#F59E0B' : '#FEF3C7',
                    color: districts.includes(d) ? '#fff' : '#92400E',
                    border: `1px solid ${districts.includes(d) ? '#F59E0B' : '#FDE68A'}`,
                    fontWeight: districts.includes(d) ? 600 : 400,
                  }}
                >{d}</button>
              ))}
            </div>
          </div>

          {/* Interests */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#374151', marginBottom: 6 }}>Interests (any match)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(MEMBER_INTERESTS || []).map(i => (
                <button
                  key={i.value} type="button"
                  onClick={() => toggleItem(interests, setInterests, i.value)}
                  style={{
                    padding: '3px 10px', borderRadius: 12, fontSize: 11, cursor: 'pointer',
                    background: interests.includes(i.value) ? '#F59E0B' : '#FEF3C7',
                    color: interests.includes(i.value) ? '#fff' : '#92400E',
                    border: `1px solid ${interests.includes(i.value) ? '#F59E0B' : '#FDE68A'}`,
                    fontWeight: interests.includes(i.value) ? 600 : 400,
                  }}
                >{i.label}</button>
              ))}
            </div>
          </div>

          {/* Employment statuses */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#374151', marginBottom: 6 }}>Employment Status (any match)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(MEMBER_EMPLOYMENT_STATUSES || []).map(s => (
                <button
                  key={s.value} type="button"
                  onClick={() => toggleItem(empStatuses, setEmpStatuses, s.value)}
                  style={{
                    padding: '3px 10px', borderRadius: 12, fontSize: 11, cursor: 'pointer',
                    background: empStatuses.includes(s.value) ? '#F59E0B' : '#FEF3C7',
                    color: empStatuses.includes(s.value) ? '#fff' : '#92400E',
                    border: `1px solid ${empStatuses.includes(s.value) ? '#F59E0B' : '#FDE68A'}`,
                    fontWeight: empStatuses.includes(s.value) ? 600 : 400,
                  }}
                >{s.label}</button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              type="button"
              className="bb-btn-secondary"
              style={{ fontSize: 13, padding: '6px 16px' }}
              onClick={handleEstimate}
              disabled={loading}
            >
              {loading ? 'Estimating…' : 'Estimate Audience'}
            </button>
            {(districts.length > 0 || interests.length > 0 || empStatuses.length > 0) && (
              <button
                type="button"
                style={{ fontSize: 12, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                onClick={() => { setDistricts([]); setInterests([]); setEmpStatuses([]); setResult(null); }}
              >
                Clear all
              </button>
            )}
            {result && (
              <div style={{ background: '#fff', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 16px', fontSize: 14 }}>
                {districts.length === 0 && interests.length === 0 && empStatuses.length === 0 ? (
                  <span>Total members in your org: <strong>{result.total}</strong></span>
                ) : (
                  <span>
                    Estimated audience: <strong style={{ color: '#D97706', fontSize: 18 }}>{result.filtered}</strong>
                    <span style={{ color: '#6B7280', fontSize: 12, marginLeft: 6 }}>out of {result.total} total members</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Moderation Queue (Event Proposals) ──────────────────────── */
function ModerationQueue({ orgId, adminId }) {
  const [proposals, setProposals]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filter, setFilter]           = useState('PROPOSAL_PENDING');
  const [actionLoading, setAction]    = useState(false);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [msg, setMsg]   = useState('');
  const [error, setError] = useState('');

  const showMsg   = (m) => { setMsg(m);   setTimeout(() => setMsg(''), 4000); };
  const showError = (m) => { setError(m); setTimeout(() => setError(''), 6000); };

  const load = useCallback(async (status) => {
    setLoading(true);
    try { setProposals(await getEventProposals(orgId, status || null)); }
    catch { showError('Failed to load proposals.'); }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(filter); }, [load, filter]);

  const handleApprove = async (p) => {
    if (!window.confirm(`Approve "${p.title}" and create a draft event?`)) return;
    setAction(true);
    try {
      const eventId = await approveProposal(orgId, p.id, adminId);
      showMsg(`Approved! Draft event created (ID: ${eventId}).`);
      load(filter);
    } catch (e) { showError(e.message || 'Approval failed.'); }
    finally { setAction(false); }
  };

  const handleDecline = async () => {
    if (!declineTarget) return;
    setAction(true);
    try {
      await declineProposal(orgId, declineTarget.id, adminId, declineReason);
      showMsg(`"${declineTarget.title}" has been declined.`);
      setDeclineTarget(null); setDeclineReason('');
      load(filter);
    } catch (e) { showError(e.message || 'Decline failed.'); }
    finally { setAction(false); }
  };

  const fmtDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const PROPOSAL_LABEL = {
    PROPOSAL_PENDING:  'Pending',
    PROPOSAL_APPROVED: 'Approved',
    PROPOSAL_DECLINED: 'Declined',
  };

  const BADGE = {
    PROPOSAL_PENDING:  { label: 'Pending',  cls: 'bb-badge bb-badge--orange' },
    PROPOSAL_APPROVED: { label: 'Approved', cls: 'bb-badge bb-badge--green'  },
    PROPOSAL_DECLINED: { label: 'Declined', cls: 'bb-badge bb-badge--red'    },
  };

  return (
    <div>
      <h3 style={{ marginTop: 24, fontFamily: 'var(--bb-font-heading)', fontSize: 20, fontWeight: 700, color: '#0F766E' }}>Event Proposal Moderation Queue</h3>
      <p style={{ fontSize: 13, color: '#64748B', marginBottom: 16 }}>
        Volunteers can submit event proposals here. Approve to create a draft event, or decline with feedback.
      </p>

      {error && <div className="bb-alert bb-alert--error" style={{ marginBottom: 12 }}>{error}</div>}
      {msg   && <div className="bb-alert bb-alert--success" style={{ marginBottom: 12 }}>{msg}</div>}

      {/* Filter buttons */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {['PROPOSAL_PENDING', 'PROPOSAL_APPROVED', 'PROPOSAL_DECLINED', null].map(s => (
          <button
            key={s || 'ALL'}
            onClick={() => setFilter(s)}
            style={{
              padding: '5px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
              background: filter === s ? 'var(--org-primary, #0F766E)' : '#F1F5F9',
              color: filter === s ? '#fff' : '#475569',
              border: `1px solid ${filter === s ? 'var(--org-primary, #0F766E)' : '#E2E8F0'}`,
              fontWeight: filter === s ? 600 : 400,
              fontFamily: 'var(--bb-font-body, sans-serif)',
            }}
          >
            {s ? PROPOSAL_LABEL[s] : 'All'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bb-loading-spinner" />
      ) : proposals.length === 0 ? (
        <div className="bb-empty-state" style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
          {filter === 'PROPOSAL_PENDING' ? 'No pending proposals. Volunteers can submit proposals from their portal.' : 'No proposals found.'}
        </div>
      ) : (
        <table className="bb-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Submitted by</th>
              <th>Category</th>
              <th>Proposed Date</th>
              <th>Capacity (M / V)</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {proposals.map(p => {
              const badge = BADGE[p.status] || { label: p.status, cls: 'bb-badge bb-badge--grey' };
              const catLabel = EVENT_CATEGORIES.find(c => c.value === p.category)?.label || p.category;
              return (
                <tr key={p.id}>
                  <td>
                    <strong>{p.title}</strong>
                    {p.description && (
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.description}
                      </div>
                    )}
                  </td>
                  <td>{p.submitted_by_name || '—'}</td>
                  <td>{catLabel}</td>
                  <td>{fmtDate(p.start_datetime)}</td>
                  <td>
                    {p.member_capacity ?? '—'} / {p.volunteer_capacity ?? '—'}
                  </td>
                  <td><span className={badge.cls}>{badge.label}</span></td>
                  <td>
                    {p.status === 'PROPOSAL_PENDING' && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          className="bb-btn-sm"
                          style={{ background: '#059669', color: '#fff' }}
                          onClick={() => handleApprove(p)}
                          disabled={actionLoading}
                        >
                          Approve
                        </button>
                        <button
                          className="bb-btn-danger-sm"
                          onClick={() => { setDeclineTarget(p); setDeclineReason(''); }}
                          disabled={actionLoading}
                        >
                          Decline
                        </button>
                      </div>
                    )}
                    {p.status === 'PROPOSAL_APPROVED' && (
                      <span style={{ fontSize: 12, color: '#059669' }}>✓ Converted to draft event</span>
                    )}
                    {p.status === 'PROPOSAL_DECLINED' && p.decline_reason && (
                      <span style={{ fontSize: 11, color: '#9CA3AF' }}>{p.decline_reason}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Decline modal */}
      {declineTarget && (
        <div className="bb-modal-overlay">
          <div className="bb-modal">
            <h3>Decline Proposal</h3>
            <p>Decline <strong>{declineTarget.title}</strong> by {declineTarget.submitted_by_name}?</p>
            <textarea
              className="bb-textarea" rows={3} style={{ width: '100%', marginTop: 8 }}
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              placeholder="Reason for declining (optional, but sent to the volunteer)..."
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button className="bb-btn-outline-sm" onClick={() => { setDeclineTarget(null); setDeclineReason(''); }}>Cancel</button>
              <button className="bb-btn-danger-sm" style={{ padding: '8px 16px' }} onClick={handleDecline} disabled={actionLoading}>
                {actionLoading ? 'Declining…' : 'Confirm Decline'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBar({ activeTab, onSelect }) {
  return (
    <div className="bb-prog-tabs">
      {TABS.map(t => (
        <button
          key={t.key}
          onClick={() => onSelect(t.key)}
          className={`bb-prog-tab${activeTab === t.key ? ' active' : ''}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export default function EventSetup({ orgId, adminId, adminName, onNavigateToEmail, onNavigateToPush, onNavigateToWhatsApp }) {
  const [activeTab, setActiveTab] = useState('allEvents');

  // All Events state
  const [events, setEvents]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Action modals
  const [showCancelModal, setShowCancelModal]   = useState(false);
  const [cancelTarget, setCancelTarget]         = useState(null);
  const [cancelReason, setCancelReason]         = useState('');
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishTarget, setPublishTarget]       = useState(null);
  const [publishError, setPublishError]         = useState('');
  const [actionLoading, setActionLoading]       = useState(false);

  // Create/Edit form state
  const [editingEvent, setEditingEvent]   = useState(null); // null = create mode
  const [form, setForm]                   = useState(EMPTY_FORM);
  const [formLoading, setFormLoading]     = useState(false);

  // Recurring events state
  const [recurringEvents, setRecurringEvents]         = useState([]);
  const [recurringLoading, setRecurringLoading]       = useState(false);
  const [generatingFor, setGeneratingFor]             = useState(null);

  // Archive state
  const [archivedEvents, setArchivedEvents]   = useState([]);
  const [archiveLoading, setArchiveLoading]   = useState(false);

  // QR & Comms state
  const [qrEvents, setQrEvents]             = useState([]);
  const [qrEventsLoading, setQrEventsLoading] = useState(false);
  const [qrSelectedId, setQrSelectedId]     = useState('');
  const [qrMsg, setQrMsg]                   = useState({ title: '', body: '' });

  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };
  const showError   = (msg) => { setError(msg);   setTimeout(() => setError(''), 5000); };

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getEvents(orgId);
      setEvents(data || []);
    } catch (e) {
      showError(e.message || 'Failed to load events.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const loadRecurring = useCallback(async () => {
    setRecurringLoading(true);
    try {
      const all = await getEvents(orgId);
      const recurring = all.filter(e => e.event_type === 'RECURRING');
      // Count instances for each
      const withCounts = recurring.map(e => ({
        ...e,
        instanceCount: all.filter(x => x.parent_event_id === e.id).length,
      }));
      setRecurringEvents(withCounts);
    } catch (e) {
      showError(e.message || 'Failed to load recurring events.');
    } finally {
      setRecurringLoading(false);
    }
  }, [orgId]);

  const loadArchived = useCallback(async () => {
    setArchiveLoading(true);
    try {
      const data = await getEvents(orgId, 'ARCHIVED');
      setArchivedEvents(data || []);
    } catch (e) {
      showError(e.message || 'Failed to load archive.');
    } finally {
      setArchiveLoading(false);
    }
  }, [orgId]);

  const loadQrEvents = useCallback(async () => {
    setQrEventsLoading(true);
    try {
      const all = await getEvents(orgId);
      setQrEvents(all.filter(e => e.status !== 'ARCHIVED'));
    } catch (e) {
      showError(e.message || 'Failed to load events.');
    } finally {
      setQrEventsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (activeTab === 'allEvents') loadEvents();
    if (activeTab === 'qrComms')   loadQrEvents();
    // moderationQueue mounts its own ModerationQueue component that self-loads
  }, [activeTab, loadEvents, loadQrEvents]);

  // ── Form helpers ──────────────────────────────────────────────────────────────
  const setF = (field, value) => setForm(p => ({ ...p, [field]: value }));
  const setLoc = (field, value) => setForm(p => ({ ...p, location: { ...p.location, [field]: value } }));
  const setSpeaker = (field, value) => setForm(p => ({ ...p, speaker: { ...p.speaker, [field]: value } }));
  const setRRule = (field, value) => setForm(p => ({ ...p, recurrence_rule: { ...p.recurrence_rule, [field]: value } }));

  const openEditMode = async (eventId) => {
    setFormLoading(true);
    try {
      const evt = await getEvent(orgId, eventId);
      if (!evt) return;
      const startDt = evt.start_datetime?.toDate ? evt.start_datetime.toDate() : new Date(evt.start_datetime);
      const endDt   = evt.end_datetime?.toDate   ? evt.end_datetime.toDate()   : new Date(evt.end_datetime);
      const toLocal = (d) => {
        const pad = n => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      };
      setForm({
        title: evt.title || '',
        category: evt.category || 'CIRCLE_TIME',
        event_type: evt.event_type || 'ONE_OFF',
        start_datetime: evt.start_datetime ? toLocal(startDt) : '',
        end_datetime:   evt.end_datetime   ? toLocal(endDt)   : '',
        location: evt.location || { name: '', address: '', postal_code: '' },
        member_capacity:           evt.member_capacity    != null ? evt.member_capacity    : '',
        volunteer_capacity:        evt.volunteer_capacity != null ? evt.volunteer_capacity : '',
        attendee_tracking_enabled: evt.attendee_tracking_enabled ?? false,
        description: evt.description || '',
        tags: (evt.tags || []).join(', '),
        photo_consent_required: evt.photo_consent_required || false,
        no_show_threshold: evt.no_show_threshold || 3,
        volunteers_required: evt.volunteers_required || false,
        volunteers_needed: evt.volunteers_needed || '',
        speaker: evt.speaker || { name: '', bio: '', headshot_url: '' },
        recurrence_rule: evt.recurrence_rule || { frequency: 'MONTHLY', day_of_week: 'Monday', nth_occurrence: 1 },
      });
      setEditingEvent(evt);
      setActiveTab('createEvent');
    } catch (e) {
      showError(e.message || 'Failed to load event for editing.');
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingEvent(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');
    try {
      const payload = {
        title: form.title,
        category: form.category,
        event_type: form.event_type,
        start_datetime: form.start_datetime ? new Date(form.start_datetime) : null,
        end_datetime:   form.end_datetime   ? new Date(form.end_datetime)   : null,
        location: form.location,
        member_capacity:    form.member_capacity    !== '' ? parseInt(form.member_capacity, 10)    : null,
        volunteer_capacity: form.volunteer_capacity !== '' ? parseInt(form.volunteer_capacity, 10) : null,
        attendee_tracking_enabled: form.attendee_tracking_enabled,
        description: form.description,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        photo_consent_required: form.photo_consent_required,
        no_show_threshold: parseInt(form.no_show_threshold, 10) || 3,
        volunteers_required: form.volunteers_required,
        volunteers_needed: form.volunteers_required ? (parseInt(form.volunteers_needed, 10) || 0) : 0,
      };
      if (form.category === 'TALK_WORKSHOP') {
        payload.speaker = form.speaker;
      }
      if (form.event_type === 'RECURRING') {
        payload.recurrence_rule = form.recurrence_rule;
      }

      if (editingEvent) {
        const { notify, notifyError } = await updateEvent(orgId, editingEvent.id, payload);
        if (notifyError) {
          showError(`Event updated, but registrants could not be notified of the change: ${notifyError}`);
        } else if (notify?.notified > 0) {
          const s = notify.summary;
          showSuccess(`Event updated. ${notify.notified} registrant(s) notified of the change (${s.email.sent} email, ${s.push.sent} push sent).`);
        } else {
          showSuccess('Event updated successfully.');
        }
      } else {
        await createEvent(orgId, payload, adminId);
        showSuccess('Event created successfully.');
      }
      resetForm();
      setActiveTab('allEvents');
    } catch (e) {
      showError(e.message || 'Failed to save event.');
    } finally {
      setFormLoading(false);
    }
  };

  // ── Row actions ───────────────────────────────────────────────────────────────
  const handlePublish = async () => {
    if (!publishTarget) return;
    setActionLoading(true);
    setPublishError('');
    const targetId    = publishTarget.id;
    const targetTitle = publishTarget.title;
    console.log('[Publish] event id =', targetId, '| title =', targetTitle, '| orgId =', orgId);
    try {
      await publishEvent(orgId, targetId);
      console.log('[Publish] success');
      setEvents(prev => prev.map(e =>
        e.id === targetId ? { ...e, status: 'PUBLISHED' } : e
      ));
      showSuccess(`"${targetTitle}" published successfully.`);
      setShowPublishModal(false);
      setPublishTarget(null);
      setPublishError('');
    } catch (e) {
      console.error('[Publish] FAILED:', e);
      setPublishError(e.message || 'Failed to publish event.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClone = async (evt) => {
    if (!window.confirm(`Clone "${evt.title}"?`)) return;
    try {
      await cloneEvent(orgId, evt.id, adminId);
      showSuccess('Event cloned as DRAFT.');
      await loadEvents();
    } catch (e) {
      showError(e.message || 'Failed to clone event.');
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget || !cancelReason.trim()) return;
    setActionLoading(true);
    try {
      await cancelEvent(orgId, cancelTarget.id, cancelReason, adminId);
      showSuccess(`"${cancelTarget.title}" cancelled.`);
      setShowCancelModal(false);
      setCancelTarget(null);
      setCancelReason('');
      await loadEvents();
    } catch (e) {
      showError(e.message || 'Failed to cancel event.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async (evt) => {
    if (!window.confirm(`Mark "${evt.title}" as Completed?`)) return;
    try {
      await completeEvent(orgId, evt.id);
      showSuccess('Event marked as completed.');
      await loadEvents();
    } catch (e) {
      showError(e.message || 'Failed to complete event.');
    }
  };

  const handleArchive = async (evt) => {
    if (!window.confirm(`Archive "${evt.title}"?`)) return;
    try {
      await archiveEvent(orgId, evt.id);
      showSuccess('Event archived.');
      await loadEvents();
    } catch (e) {
      showError(e.message || 'Failed to archive event.');
    }
  };

  const handleGenerateInstances = async (evt) => {
    if (!window.confirm(`Generate 12 recurring instances for "${evt.title}"?`)) return;
    setGeneratingFor(evt.id);
    try {
      const ids = await generateRecurringInstances(orgId, evt.id, 12);
      showSuccess(`Generated ${ids.length} instances.`);
      await loadRecurring();
    } catch (e) {
      showError(e.message || 'Failed to generate instances.');
    } finally {
      setGeneratingFor(null);
    }
  };

  const filteredEvents = events.filter(e => {
    if (statusFilter   && e.status   !== statusFilter)   return false;
    if (categoryFilter && e.category !== categoryFilter) return false;
    if (searchTerm && !e.title?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return e.status !== 'ARCHIVED';
  });

  return (
    <div className="bb-prog-tool">
      <div className="bb-prog-tool-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2>Event Setup</h2>
          <button
            className="bb-btn-primary"
            style={{ marginRight: 5 }}
            onClick={() => { resetForm(); setActiveTab('createEvent'); }}
          >
            + Create Event
          </button>
        </div>
        <p>Create, manage, and configure events for your organisation.</p>
      </div>

      <TabBar activeTab={activeTab} onSelect={(t) => { setActiveTab(t); if (t !== 'createEvent') resetForm(); }} />

      {error   && <div className="bb-alert bb-alert--error">{error}</div>}
      {success && <div className="bb-alert bb-alert--success">{success}</div>}

      {/* ── ALL EVENTS ── */}
      {activeTab === 'allEvents' && (
        <div>
          {/* Filter bar */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <select className="bb-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ minWidth: 140 }}>
              <option value="">All Statuses</option>
              {EVENT_STATUSES.filter(s => s !== 'ARCHIVED').map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="bb-select" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={{ minWidth: 180 }}>
              <option value="">All Categories</option>
              {EVENT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <input
              className="bb-input"
              placeholder="Search by title..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ minWidth: 200 }}
            />
            <button className="bb-btn-primary" onClick={() => { resetForm(); setActiveTab('createEvent'); }}>+ Create Event</button>
          </div>

          {loading ? (
            <div className="bb-loading-spinner" />
          ) : filteredEvents.length === 0 ? (
            <div className="bb-empty-state" style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>No events found.</div>
          ) : (
            <table className="bb-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Start Date</th>
                  <th>Status</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Vol. Required</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Vol. Signed Up</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map(evt => (
                  <tr key={evt.id}>
                    <td><strong>{evt.title}</strong></td>
                    <td>
                      <span className={CATEGORY_BADGE[evt.category] || 'bb-badge bb-badge--grey'}>
                        {EVENT_CATEGORIES.find(c => c.value === evt.category)?.label || evt.category}
                      </span>
                    </td>
                    <td>{fmtDateTime(evt.start_datetime)}</td>
                    <td><span className={STATUS_BADGE[evt.status] || 'bb-badge bb-badge--grey'}>{evt.status}</span></td>
                    <td style={{ textAlign: 'center' }}>
                      {evt.volunteers_required
                        ? <span style={{ fontWeight: 600, color: '#92400E' }}>{evt.volunteers_needed || 0}</span>
                        : <span style={{ color: '#D1D5DB' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {evt.volunteers_required
                        ? <span style={{ fontWeight: 600, color: (evt.volunteer_signup_count || 0) >= (evt.volunteers_needed || 0) ? '#059669' : '#6B46C1' }}>
                            {evt.volunteer_signup_count || 0}
                          </span>
                        : <span style={{ color: '#D1D5DB' }}>—</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button className="bb-btn-sm" onClick={() => openEditMode(evt.id)}>Edit</button>
                        {evt.status === 'DRAFT' && (
                          <button className="bb-btn-sm" style={{ background: '#059669', color: '#fff' }}
                            onClick={() => { setPublishTarget(evt); setShowPublishModal(true); }}>
                            Publish
                          </button>
                        )}
                        <button className="bb-btn-outline-sm" onClick={() => handleClone(evt)}>Clone</button>
                        {evt.status === 'PUBLISHED' && (
                          <>
                            <button className="bb-btn-danger-sm" onClick={() => { setCancelTarget(evt); setShowCancelModal(true); }}>Cancel</button>
                            <button className="bb-btn-sm" style={{ background: '#3B82F6', color: '#fff' }} onClick={() => handleComplete(evt)}>Complete</button>
                          </>
                        )}
                        {(evt.status === 'COMPLETED' || evt.status === 'CANCELLED') && (
                          <button className="bb-btn-outline-sm" onClick={() => handleArchive(evt)}>Archive</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── CREATE / EDIT EVENT ── */}
      {activeTab === 'createEvent' && (
        <div style={{ paddingLeft: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0 }}>{editingEvent ? `Edit: ${editingEvent.title}` : 'Create New Event'}</h3>
            <button className="bb-btn-outline-sm" onClick={resetForm}>Reset</button>
          </div>

          {formLoading && <div className="bb-loading-spinner" />}

          <form onSubmit={handleSubmit} style={{ display: formLoading ? 'none' : 'block' }}>
            <div className="bb-form-row">
              <div className="bb-form-group">
                <label>Title <span style={{ color: '#DC2626' }}>*</span></label>
                <input className="bb-input" required value={form.title} onChange={e => setF('title', e.target.value)} placeholder="Event title" />
              </div>
              <div className="bb-form-group">
                <label>Category</label>
                <select className="bb-select" value={form.category} onChange={e => setF('category', e.target.value)}>
                  {EVENT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>

            <div className="bb-form-row">
              <div className="bb-form-group">
                <label>Event Type</label>
                <select className="bb-select" value={form.event_type} onChange={e => setF('event_type', e.target.value)}>
                  <option value="ONE_OFF">One-Off</option>
                  <option value="RECURRING">Recurring</option>
                </select>
              </div>
            </div>

            {/* Dual-capacity fields */}
            <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 8, padding: 16, marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#0C4A6E', marginBottom: 10 }}>
                🎟️ Capacity by Type
                <span style={{ fontWeight: 400, fontSize: 12, color: '#6B7280', marginLeft: 8 }}>
                  Set separate limits for member attendees and volunteer attendees.
                </span>
              </div>
              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label>Member Capacity</label>
                  <input
                    className="bb-input"
                    type="number" min={0}
                    value={form.member_capacity}
                    onChange={e => setF('member_capacity', e.target.value)}
                    placeholder="e.g. 25"
                  />
                  <span style={{ fontSize: 11, color: '#9CA3AF' }}>Slots available for member attendees</span>
                </div>
                <div className="bb-form-group">
                  <label>Volunteer Attendee Capacity</label>
                  <input
                    className="bb-input"
                    type="number" min={0}
                    value={form.volunteer_capacity}
                    onChange={e => setF('volunteer_capacity', e.target.value)}
                    placeholder="e.g. 5"
                  />
                  <span style={{ fontSize: 11, color: '#9CA3AF' }}>Slots for volunteers registering as attendees</span>
                </div>
              </div>
              <div className="bb-form-group" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0 }}>
                <input
                  type="checkbox"
                  id="attendeeTracking"
                  checked={form.attendee_tracking_enabled}
                  onChange={e => setF('attendee_tracking_enabled', e.target.checked)}
                />
                <label htmlFor="attendeeTracking" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 13 }}>
                  Enable per-attendee QR code tracking
                  <span style={{ marginLeft: 6, fontSize: 11, color: '#6B7280' }}>
                    (generates unique QR for each registration)
                  </span>
                </label>
              </div>
            </div>

            {/* Audience estimation panel */}
            <AudienceEstimation orgId={orgId} />

            <div className="bb-form-row">
              <div className="bb-form-group">
                <label>Start Date & Time <span style={{ color: '#DC2626' }}>*</span></label>
                <input className="bb-input" required type="datetime-local" value={form.start_datetime} onChange={e => setF('start_datetime', e.target.value)} />
              </div>
              <div className="bb-form-group">
                <label>End Date & Time <span style={{ color: '#DC2626' }}>*</span></label>
                <input className="bb-input" required type="datetime-local" value={form.end_datetime} onChange={e => setF('end_datetime', e.target.value)} />
              </div>
            </div>

            <div className="bb-form-row">
              <div className="bb-form-group">
                <label>Location Name</label>
                <input className="bb-input" value={form.location.name} onChange={e => setLoc('name', e.target.value)} placeholder="e.g. Community Hall A" />
              </div>
              <div className="bb-form-group">
                <label>Location Address</label>
                <input className="bb-input" value={form.location.address} onChange={e => setLoc('address', e.target.value)} placeholder="Street address" />
              </div>
              <div className="bb-form-group">
                <label>Postal Code</label>
                <input className="bb-input" value={form.location.postal_code} onChange={e => setLoc('postal_code', e.target.value)} placeholder="e.g. 570123" />
              </div>
            </div>

            <div className="bb-form-group">
              <label>Description</label>
              <textarea className="bb-textarea" rows={4} value={form.description} onChange={e => setF('description', e.target.value)} placeholder="Event description..." />
            </div>

            <div className="bb-form-row">
              <div className="bb-form-group">
                <label>Tags <span style={{ fontSize: 12, color: '#9CA3AF' }}>(comma separated)</span></label>
                <input className="bb-input" value={form.tags} onChange={e => setF('tags', e.target.value)} placeholder="e.g. health, wellness, community" />
              </div>
              <div className="bb-form-group">
                <label>No-Show Threshold</label>
                <input className="bb-input" type="number" min={1} value={form.no_show_threshold} onChange={e => setF('no_show_threshold', e.target.value)} />
              </div>
            </div>

            <div className="bb-form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="photoConsent" checked={form.photo_consent_required} onChange={e => setF('photo_consent_required', e.target.checked)} />
              <label htmlFor="photoConsent" style={{ marginBottom: 0, cursor: 'pointer' }}>Photo Consent Required</label>
            </div>

            {/* Volunteer requirements */}
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: 16, marginTop: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 10, color: '#92400E', fontSize: 13 }}>Volunteer Requirements</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: form.volunteers_required ? 12 : 0 }}>
                <input
                  type="checkbox"
                  id="volRequired"
                  checked={form.volunteers_required}
                  onChange={e => { setF('volunteers_required', e.target.checked); if (!e.target.checked) setF('volunteers_needed', ''); }}
                />
                <label htmlFor="volRequired" style={{ marginBottom: 0, cursor: 'pointer', fontWeight: 500 }}>
                  Volunteers Required for this Event
                </label>
              </div>
              {form.volunteers_required && (
                <div className="bb-form-group" style={{ maxWidth: 200, marginBottom: 0 }}>
                  <label>Number of Volunteers Needed <span style={{ color: '#DC2626' }}>*</span></label>
                  <input
                    className="bb-input"
                    type="number"
                    min={1}
                    required={form.volunteers_required}
                    value={form.volunteers_needed}
                    onChange={e => setF('volunteers_needed', e.target.value)}
                    placeholder="e.g. 3"
                  />
                </div>
              )}
            </div>

            {/* Speaker fields — TALK_WORKSHOP only */}
            {form.category === 'TALK_WORKSHOP' && (
              <div style={{ background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 8, padding: 16, marginTop: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 12, color: '#5B21B6' }}>Speaker Details</div>
                <div className="bb-form-row">
                  <div className="bb-form-group">
                    <label>Speaker Name</label>
                    <input className="bb-input" value={form.speaker.name} onChange={e => setSpeaker('name', e.target.value)} placeholder="Full name" />
                  </div>
                  <div className="bb-form-group">
                    <label>Speaker Headshot URL</label>
                    <input className="bb-input" value={form.speaker.headshot_url} onChange={e => setSpeaker('headshot_url', e.target.value)} placeholder="https://..." />
                  </div>
                </div>
                <div className="bb-form-group">
                  <label>Speaker Bio</label>
                  <textarea className="bb-textarea" rows={3} value={form.speaker.bio} onChange={e => setSpeaker('bio', e.target.value)} placeholder="Brief biography..." />
                </div>
              </div>
            )}

            {/* Recurrence fields */}
            {form.event_type === 'RECURRING' && (
              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: 16, marginTop: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 12, color: '#166534' }}>Recurrence Settings</div>
                <div className="bb-form-row">
                  <div className="bb-form-group">
                    <label>Frequency</label>
                    <select className="bb-select" value={form.recurrence_rule.frequency} onChange={e => setRRule('frequency', e.target.value)}>
                      <option value="MONTHLY">Monthly</option>
                      <option value="WEEKLY">Weekly</option>
                    </select>
                  </div>
                  <div className="bb-form-group">
                    <label>Day of Week</label>
                    <select className="bb-select" value={form.recurrence_rule.day_of_week} onChange={e => setRRule('day_of_week', e.target.value)}>
                      {DAY_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="bb-form-group">
                    <label>Nth Occurrence</label>
                    <select className="bb-select" value={form.recurrence_rule.nth_occurrence} onChange={e => setRRule('nth_occurrence', parseInt(e.target.value))}>
                      {[1,2,3,4,5].map(n => <option key={n} value={n}>{n === 5 ? 'Last' : `${n}${['st','nd','rd','th','th'][n-1]}`}</option>)}
                    </select>
                  </div>
                </div>
                {editingEvent && (
                  <button type="button" className="bb-btn-secondary"
                    onClick={() => handleGenerateInstances(editingEvent)}
                    disabled={generatingFor === editingEvent?.id}>
                    {generatingFor === editingEvent?.id ? 'Generating...' : 'Generate 12 Instances'}
                  </button>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button className="bb-btn-primary" type="submit" disabled={formLoading}>
                {formLoading ? 'Saving...' : editingEvent ? 'Update Event' : 'Create Event'}
              </button>
              <button className="bb-btn-outline-sm" type="button" onClick={() => { resetForm(); setActiveTab('allEvents'); }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── QR & COMMS ── */}
      {activeTab === 'qrComms' && (
        <div style={{ padding: '0 4px' }}>
          {/* ── QR Code section ── */}
          <div style={{ background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 10, padding: 20, marginBottom: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#5B21B6', marginBottom: 4 }}>Event Registration QR Code</div>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6B7280' }}>
              Select an event to generate a QR code and link that anyone can use to register — no login needed.
            </p>
            <div className="bb-form-group" style={{ maxWidth: 440 }}>
              <label>Select Event</label>
              {qrEventsLoading ? (
                <div className="bb-loading-spinner" style={{ width: 24, height: 24, borderWidth: 2 }} />
              ) : (
                <select
                  className="bb-select"
                  value={qrSelectedId}
                  onChange={e => setQrSelectedId(e.target.value)}
                >
                  <option value="">-- Select an event --</option>
                  {qrEvents.map(evt => (
                    <option key={evt.id} value={evt.id}>
                      {evt.title} ({fmtDateTime(evt.start_datetime)})
                    </option>
                  ))}
                </select>
              )}
            </div>
            {qrSelectedId && (() => {
              const evt = qrEvents.find(e => e.id === qrSelectedId);
              const regUrl = getRegistrationUrl(orgId, qrSelectedId);
              return (<>
                <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <img
                      src={getQRCodeUrl(regUrl, 200)}
                      alt="Registration QR Code"
                      style={{ display: 'block', borderRadius: 8, border: '1px solid #E5E7EB', marginBottom: 8 }}
                    />
                    <a
                      href={getQRCodeUrl(regUrl, 400)}
                      download={`${(evt?.title || 'event').replace(/\s+/g, '-')}-qr.png`}
                      target="_blank"
                      rel="noreferrer"
                      className="bb-btn-outline-sm"
                      style={{ display: 'inline-block', textDecoration: 'none', marginTop: 4 }}
                    >
                      Download QR
                    </a>
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>Registration Link</div>
                    <div style={{
                      background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6,
                      padding: '8px 10px', fontSize: 12, color: '#6B46C1',
                      wordBreak: 'break-all', fontFamily: 'monospace', marginBottom: 8
                    }}>
                      {regUrl}
                    </div>
                    <div style={{ fontSize: 13, color: '#374151' }}>
                      <strong>Event:</strong> {evt?.title}<br />
                      <strong>Date:</strong> {fmtDateTime(evt?.start_datetime)}<br />
                      <strong>Venue:</strong> {evt?.location?.name || '—'}
                    </div>
                    {evt?.status !== 'PUBLISHED' && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#B45309' }}>
                        This event is {evt?.status?.toLowerCase()}. The link only accepts registrations once it is published.
                      </div>
                    )}
                  </div>
                </div>
                {evt && (
                  <RegistrationFormBuilder
                    key={evt.id}
                    orgId={orgId}
                    event={evt}
                    onSaved={(id, fields) => setQrEvents(list => list.map(e => (e.id === id ? { ...e, registration_form: { fields } } : e)))}
                  />
                )}
              </>);
            })()}
          </div>

          {/* ── Message Composer ── */}
          <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#166534', marginBottom: 4 }}>Event Message Composer</div>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6B7280' }}>
              Draft a message for this event, then choose a channel to send it to your members.
            </p>
            <div className="bb-form-group">
              <label>Message Title</label>
              <input
                className="bb-input"
                value={qrMsg.title}
                onChange={e => setQrMsg(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Join Us: Financial Literacy Workshop on 10 May"
                style={{ maxWidth: 540 }}
              />
            </div>
            <div className="bb-form-group">
              <label>Message Body</label>
              <textarea
                className="bb-textarea"
                rows={5}
                value={qrMsg.body}
                onChange={e => setQrMsg(p => ({ ...p, body: e.target.value }))}
                placeholder="Write your event announcement here — include key details like date, time, venue, and how to register..."
                style={{ maxWidth: 540 }}
              />
            </div>

            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
                Send via a communication channel:
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  className="bb-btn-primary"
                  type="button"
                  onClick={() => onNavigateToEmail && onNavigateToEmail()}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#2563EB' }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <rect x="1" y="3" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M1 5l7 5 7-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  Email Campaign →
                </button>
                <button
                  className="bb-btn-primary"
                  type="button"
                  onClick={() => onNavigateToPush && onNavigateToPush()}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#7C3AED' }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M8 2a5 5 0 014.9 4l.1.5v3l1.5 2H1.5L3 9.5v-3A5 5 0 018 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                    <path d="M6.5 13.5a1.5 1.5 0 003 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  Push Notification →
                </button>
                <button
                  className="bb-btn-primary"
                  type="button"
                  onClick={() => onNavigateToWhatsApp && onNavigateToWhatsApp()}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#059669' }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M5.5 7.5c.5 1.5 2 3 3.5 3.5l1-1-1.5-1-1 .5c-.5-.5-1-1-1.5-1.5l.5-1L5 6.5l-1 1z" fill="currentColor"/>
                  </svg>
                  WhatsApp Campaign →
                </button>
              </div>
              <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 10 }}>
                Clicking a channel button will take you to that module where you can compose and send the message to your members.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── MODERATION QUEUE ── */}
      {activeTab === 'moderationQueue' && (
        <ModerationQueue orgId={orgId} adminId={adminId} />
      )}

      {/* ── Publish Confirm Modal ── */}
      {showPublishModal && publishTarget && (
        <div className="bb-modal-overlay">
          <div className="bb-modal">
            <h3>Publish Event</h3>
            <p>Are you sure you want to publish <strong>"{publishTarget.title}"</strong>?
              It will become visible to members and volunteers for registration.</p>
            {publishError && (
              <div style={{
                background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6,
                padding: '10px 14px', marginTop: 12, color: '#B91C1C', fontSize: 13,
              }}>
                ❌ {publishError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button className="bb-btn-outline-sm" onClick={() => { setShowPublishModal(false); setPublishTarget(null); setPublishError(''); }}>
                {publishError ? 'Close' : 'Cancel'}
              </button>
              {!publishError && (
                <button className="bb-btn-primary" onClick={handlePublish} disabled={actionLoading}>
                  {actionLoading ? 'Publishing…' : 'Publish'}
                </button>
              )}
              {publishError && (
                <button className="bb-btn-primary" onClick={handlePublish} disabled={actionLoading}>
                  {actionLoading ? 'Retrying…' : 'Retry'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Cancel Event Modal ── */}
      {showCancelModal && cancelTarget && (
        <div className="bb-modal-overlay">
          <div className="bb-modal">
            <h3>Cancel Event</h3>
            <p>You are about to cancel <strong>"{cancelTarget.title}"</strong>. Please provide a reason:</p>
            <textarea
              className="bb-textarea"
              rows={3}
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation..."
              style={{ width: '100%', marginTop: 8 }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button className="bb-btn-outline-sm" onClick={() => { setShowCancelModal(false); setCancelTarget(null); setCancelReason(''); }}>Back</button>
              <button className="bb-btn-danger-sm" style={{ padding: '8px 16px' }} onClick={handleCancel} disabled={actionLoading || !cancelReason.trim()}>
                {actionLoading ? 'Cancelling...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
